import { nip19 } from 'nostr-tools';

import { APP_READ_RELAY_URLS } from '@/lib/appRelays';
import type { Highlight } from '@/lib/highlights';

/** Max quote length carried in the embed URL (keeps URLs well under limits). */
const MAX_PARAM_QUOTE = 350;
const MAX_PARAM_NAME = 60;

function truncate(text: string, max: number): string {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= max) return collapsed;
  return collapsed.slice(0, max - 1).trimEnd() + '…';
}

/**
 * Build the embed URL for a highlight. Carries the quote + highlighter name
 * as query params so the embed renders meaningful content instantly, before
 * any relay responds. The nevent carries app relay hints so embed hydration
 * can fall back to direct relay queries.
 */
export function buildEmbedUrl(highlight: Highlight, highlighterName: string): string {
  const nevent = nip19.neventEncode({
    id: highlight.event.id,
    author: highlight.event.pubkey,
    relays: APP_READ_RELAY_URLS,
  });

  const params = new URLSearchParams();
  if (highlight.quote) params.set('q', truncate(highlight.quote, MAX_PARAM_QUOTE));
  if (highlighterName) params.set('by', truncate(highlighterName, MAX_PARAM_NAME));

  const query = params.toString();
  return `${window.location.origin}/embed/${nevent}${query ? `?${query}` : ''}`;
}

/** The event id used to match iframes for auto-height messages. */
export function embedDomId(highlight: Highlight): string {
  return highlight.event.id.slice(0, 12);
}

/**
 * The copy-paste snippet for blogs and CMSs. The inline script enables
 * auto-height on platforms that allow scripts (Ghost HTML cards,
 * self-hosted blogs); where scripts are stripped, the iframe simply keeps
 * its compact fixed height.
 */
export function buildEmbedSnippet(embedUrl: string, highlight: Highlight): string {
  const id = embedDomId(highlight);
  return `<iframe src="${embedUrl}" height="180" data-glowmark-embed="${id}" style="display:block;width:100%;max-width:600px;margin:1.5em auto;border:0;border-radius:12px;" loading="lazy" title="Highlighted passage via glowmark"></iframe>
<script>(function(){addEventListener("message",function(e){var d=e.data;if(!d||d.type!=="glowmark:embed"||!d.id)return;var f=document.querySelector('iframe[data-glowmark-embed="'+d.id+'"]');if(f)f.style.height=d.height+"px";});})();</script>`;
}
